import { NavLink, useLocation } from "react-router-dom";
import { Home, BookOpen, MessageSquare, Menu, X, BarChart3, Award, ShieldCheck, LogOut, Moon, Sun } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useSchoolFeatures } from "@/hooks/useSchoolFeatures";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";

interface NavItem {
  icon: React.ElementType;
  label: string;
  to: string;
  feature?: "risk_checker";
}

const primaryTabs: NavItem[] = [
  { icon: Home, label: "ホーム", to: "/student/dashboard" },
  { icon: BookOpen, label: "動画学習", to: "/student/courses" },
  { icon: MessageSquare, label: "AIに相談", to: "/student/chat" },
];

const menuItems: NavItem[] = [
  { icon: BarChart3, label: "進捗確認", to: "/student/progress" },
  { icon: ShieldCheck, label: "台本リスクチェック", to: "/student/risk-checker", feature: "risk_checker" },
  { icon: Award, label: "達成", to: "/student/achievements" },
];

export function MobileBottomNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const features = useSchoolFeatures();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const visibleMenuItems = menuItems.filter((item) => {
    return !item.feature || features[item.feature] === true;
  });

  const handleLogout = async () => {
    sessionStorage.removeItem('chat_page_visited');
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "エラー",
        description: "ログアウトに失敗しました",
        variant: "destructive",
      });
    } else {
      toast({
        title: "ログアウト成功",
        description: "またお会いしましょう",
      });
      navigate("/auth");
    }
  };

  const isActiveTab = (to: string) => {
    // ダッシュボードは完全一致（他のルートと区別するため）
    if (to === "/student/dashboard") {
      return location.pathname === to;
    }
    // その他は前方一致（サブルートもハイライト）
    return location.pathname.startsWith(to);
  };

  const isMenuActive = visibleMenuItems.some((item) => isActiveTab(item.to));

  return (
    <>
      {/* Menu Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Menu Panel */}
      <div
        className={cn(
          "fixed bottom-16 left-0 right-0 bg-background border-t z-50 md:hidden transition-transform duration-200 ease-out",
          isMenuOpen ? "translate-y-0" : "translate-y-full"
        )}
        role="dialog"
        aria-modal="true"
        aria-label="追加メニュー"
      >
        <div className="p-4 space-y-2">
          {visibleMenuItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setIsMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted"
                )
              }
            >
              <item.icon className="h-5 w-5" aria-hidden="true" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}

          <div className="border-t pt-2 mt-2 space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 px-4 py-3 h-auto"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? (
                <>
                  <Sun className="h-5 w-5" aria-hidden="true" />
                  <span>ライトモードに切替</span>
                </>
              ) : (
                <>
                  <Moon className="h-5 w-5" aria-hidden="true" />
                  <span>ダークモードに切替</span>
                </>
              )}
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start gap-3 px-4 py-3 h-auto text-destructive hover:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5" aria-hidden="true" />
              <span>ログアウト</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation Bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-background border-t z-50 md:hidden safe-area-pb"
        role="navigation"
        aria-label="モバイルナビゲーション"
      >
        <div className="flex items-center justify-around h-16">
          {primaryTabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={cn(
                "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors",
                isActiveTab(tab.to)
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <tab.icon className="h-5 w-5" aria-hidden="true" />
              <span className="text-xs font-medium">{tab.label}</span>
              {isActiveTab(tab.to) && <span className="sr-only">（現在のページ）</span>}
            </NavLink>
          ))}

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={cn(
              "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors",
              isMenuOpen || isMenuActive
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            )}
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? "メニューを閉じる" : "メニューを開く"}
          >
            {isMenuOpen ? (
              <X className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Menu className="h-5 w-5" aria-hidden="true" />
            )}
            <span className="text-xs font-medium">メニュー</span>
          </button>
        </div>
      </nav>
    </>
  );
}
